// Appointment schedules and their slots (docs/plans/phase2-appointments.md §4).
//
// A schedule's weekly sessions are East Africa wall-clock times ("08:00–12:00 on
// Mondays"). Slots are computed for one EAT calendar day and returned as UTC instants,
// which is how appointments are stored (domain rule 5). EAT is a fixed UTC+3 (time.ts), so
// a session never crosses a clock change. Sessions end before midnight: a night clinic
// is two sessions on two days.

import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';
import {
  addDays,
  compareDays,
  EAT_UTC_OFFSET_MINUTES,
  eatDayOf,
  startOfEatDay,
  type CalendarDay,
} from '../time.ts';

export interface WeeklySession {
  /** ISO weekday: Monday 1 to Sunday 7. */
  readonly weekday: number;
  /** "HH:MM", 24-hour, East Africa Time. */
  readonly start: string;
  readonly end: string;
}

export interface ScheduleShape {
  readonly slotMinutes: number;
  readonly slotCapacity: number;
  readonly weeklySessions: readonly WeeklySession[];
  readonly validFrom: CalendarDay;
  /** Inclusive; null for open-ended. */
  readonly validUntil: CalendarDay | null;
}

export interface Slot {
  readonly startsAt: Date;
  readonly endsAt: Date;
}

export interface SlotAvailability extends Slot {
  readonly capacity: number;
  readonly booked: number;
  readonly free: number;
  /** The slot has started, so it can't be booked. */
  readonly past: boolean;
}

/** Session times are on a five-minute grid, so slots line up with what people write. */
export const SESSION_STEP_MINUTES = 5;
export const MAX_WEEKLY_SESSIONS = 28;
export const SLOT_MINUTES_RANGE = { min: 5, max: 240 } as const;
export const SLOT_CAPACITY_RANGE = { min: 1, max: 50 } as const;
/** How far ahead an appointment may be booked. TODO(business): plan §12 B3. */
export const BOOKING_HORIZON_DAYS = 180;

export type ScheduleErrorCode =
  | 'appointments.sessions_empty'
  | 'appointments.too_many_sessions'
  | 'appointments.weekday_invalid'
  | 'appointments.session_time_malformed'
  | 'appointments.session_not_on_step'
  | 'appointments.session_end_before_start'
  | 'appointments.sessions_overlap';

/** English descriptions; the API and the web app own the wording shown to a user. */
export const SCHEDULE_ERRORS: Record<ScheduleErrorCode, string> = {
  'appointments.sessions_empty': 'Add at least one session',
  'appointments.too_many_sessions': `A schedule has at most ${String(MAX_WEEKLY_SESSIONS)} sessions a week`,
  'appointments.weekday_invalid': 'Choose a day of the week for each session',
  'appointments.session_time_malformed': 'Write session times as HH:MM, from 00:00 to 23:59',
  'appointments.session_not_on_step': `Session times are in steps of ${String(SESSION_STEP_MINUTES)} minutes`,
  'appointments.session_end_before_start': 'Each session must end after it starts',
  'appointments.sessions_overlap': 'Two sessions on the same day overlap',
};

const MINUTE_MS = 60_000;

/** Minutes after midnight, or null when the text isn't a valid HH:MM. */
export function minutesOfDay(text: string): number | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(text);
  if (match === null) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

/** ISO weekday of a calendar day: Monday 1 to Sunday 7. */
export function isoWeekday(day: CalendarDay): number {
  // EAT midnight shifted by the offset is UTC midnight of the same calendar day.
  const utcMidnight = startOfEatDay(day).getTime() + EAT_UTC_OFFSET_MINUTES * MINUTE_MS;
  const sundayZero = new Date(utcMidnight).getUTCDay();
  return ((sundayZero + 6) % 7) + 1;
}

/** Checks a schedule's weekly sessions and returns them sorted by day and start. */
export function validateWeeklySessions(
  sessions: readonly WeeklySession[],
): Result<WeeklySession[], ScheduleErrorCode> {
  if (sessions.length === 0) return err('appointments.sessions_empty');
  if (sessions.length > MAX_WEEKLY_SESSIONS) return err('appointments.too_many_sessions');
  const parsed: { session: WeeklySession; start: number; end: number }[] = [];
  for (const session of sessions) {
    if (!Number.isInteger(session.weekday) || session.weekday < 1 || session.weekday > 7) {
      return err('appointments.weekday_invalid');
    }
    const start = minutesOfDay(session.start);
    const end = minutesOfDay(session.end);
    if (start === null || end === null) return err('appointments.session_time_malformed');
    if (start % SESSION_STEP_MINUTES !== 0 || end % SESSION_STEP_MINUTES !== 0) {
      return err('appointments.session_not_on_step');
    }
    if (end <= start) return err('appointments.session_end_before_start');
    parsed.push({ session, start, end });
  }
  parsed.sort((a, b) => a.session.weekday - b.session.weekday || a.start - b.start);
  for (let i = 1; i < parsed.length; i += 1) {
    const previous = parsed[i - 1];
    const current = parsed[i];
    if (
      previous !== undefined &&
      previous.session.weekday === current?.session.weekday &&
      current.start < previous.end
    ) {
      return err('appointments.sessions_overlap');
    }
  }
  return ok(
    parsed.map(({ session }) => ({
      weekday: session.weekday,
      start: session.start,
      end: session.end,
    })),
  );
}

function withinValidity(schedule: ScheduleShape, day: CalendarDay): boolean {
  if (compareDays(day, schedule.validFrom) < 0) return false;
  return schedule.validUntil === null || compareDays(day, schedule.validUntil) <= 0;
}

/**
 * The slots of a schedule on one East Africa day, in time order. A part-slot at the end of
 * a session, shorter than the slot length, is left out.
 */
export function slotsForDay(schedule: ScheduleShape, day: CalendarDay): Slot[] {
  if (!withinValidity(schedule, day)) return [];
  const weekday = isoWeekday(day);
  const midnight = startOfEatDay(day).getTime();
  const slotMs = schedule.slotMinutes * MINUTE_MS;
  const slots: Slot[] = [];
  const sessions = schedule.weeklySessions
    .filter((s) => s.weekday === weekday)
    .map((s) => ({ start: minutesOfDay(s.start), end: minutesOfDay(s.end) }))
    .filter((s): s is { start: number; end: number } => s.start !== null && s.end !== null)
    .sort((a, b) => a.start - b.start);
  for (const session of sessions) {
    const end = midnight + session.end * MINUTE_MS;
    for (let start = midnight + session.start * MINUTE_MS; start + slotMs <= end; start += slotMs) {
      slots.push({ startsAt: new Date(start), endsAt: new Date(start + slotMs) });
    }
  }
  return slots;
}

/** The slot that starts at exactly this instant, on the EAT day it falls on, or null. */
export function findSlot(schedule: ScheduleShape, startsAt: Date): Slot | null {
  const time = startsAt.getTime();
  return (
    slotsForDay(schedule, eatDayOf(startsAt)).find((s) => s.startsAt.getTime() === time) ?? null
  );
}

/** Booked places per slot and what is still free. `bookings` are the active ones only. */
export function slotAvailability(
  slots: readonly Slot[],
  bookings: readonly { readonly startsAt: Date }[],
  capacity: number,
  now: Date,
): SlotAvailability[] {
  const counts = new Map<number, number>();
  for (const b of bookings) {
    const key = b.startsAt.getTime();
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return slots.map((slot) => {
    const booked = counts.get(slot.startsAt.getTime()) ?? 0;
    return {
      ...slot,
      capacity,
      booked,
      free: Math.max(0, capacity - booked),
      past: slot.startsAt.getTime() <= now.getTime(),
    };
  });
}

/** Whether an appointment starting then is within the booking horizon from now. */
export function withinBookingHorizon(startsAt: Date, now: Date): boolean {
  const lastDay = addDays(eatDayOf(now), BOOKING_HORIZON_DAYS);
  return compareDays(eatDayOf(startsAt), lastDay) <= 0;
}
